import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import Table from '../ogComponent/table';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { IconPlus } from '@tabler/icons-react';
import Spinner from '../ogComponent/spinner';

export default function Listing() {
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    const [students, setStudents] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [selectedStudent, setSelectedStudent] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [searchTimeout, setSearchTimeout] = useState(null);
    const [batches, setBatches] = useState([]);
    const [selectedCourseId, setSelectedCourseId] = useState('');
    const [currentPage, setCurrentPage] = useState(1);

    // Access permissions array from Redux store
    const permissions = useSelector((state) => state.emp.permissions);

    const pagePermissions = ["showstudentsID", "editstudentsID", "addstudentsID", "showstudentspunchID", "showstudentdetailsID"];

    // Initialize page from URL or default to 1
    useEffect(() => {
        const page = searchParams.get('page');
        if (page) {
            setCurrentPage(parseInt(page));
        }
    }, [searchParams]);

    // Fetch batches from API
    const fetchBatches = async () => {
        try {
            const accessToken = localStorage.getItem('accessToken');
            if (!accessToken) {
                throw new Error('No access token found');
            }

            const response = await fetch(`${process.env.REACT_APP_API}/api/course/batches/`, {
                method: 'GET',
                headers: {
                    'Authorization': `Bearer ${accessToken}`,
                    'Content-Type': 'application/json',
                },
            });

            if (!response.ok) {
                throw new Error('Network response was not ok');
            }

            const data = await response.json();
            setBatches(data);
        } catch (error) {
            console.error('Error fetching batches:', error);
        }
    };

    // Fetch students with optional search query and course ID
    const fetchStudents = async (query = '', courseId = '') => {
        try {
            setLoading(true);
            const accessToken = localStorage.getItem('accessToken');
            if (!accessToken) {
                throw new Error('No access token found');
            }

            const url = `${process.env.REACT_APP_API}/api/course/students/?search=${query}${courseId ? `&selected_course=${courseId}` : ''}`;

            const response = await fetch(url, {
                method: 'GET',
                headers: {
                    'Authorization': `Bearer ${accessToken}`,
                    'Content-Type': 'application/json',
                },
            });

            if (!response.ok) {
                throw new Error('Network response was not ok');
            }

            const data = await response.json();
            const formattedStudents = data.map((student, index) => ({
                id: student.id,
                count: index + 1,
                name: (
                    <td
                        className="d-flex gap-3 align-items-center px-0"
                        style={{
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                        }}
                    >
                        <img
                            style={{
                                width: "25px",
                                height: "25px",
                                flexShrink: 0,
                                borderRadius: "50%",
                            }}
                            src={student.profile_photo || "/assets/user.png"}
                            alt="user img"
                        />
                        <div style={{ overflow: "hidden", textOverflow: "ellipsis" }}>
                            {student?.full_name || "N/A"}
                        </div>
                    </td>
                ),
                email: student.email,
                contact: student.contact_number,
                fee: `${student.amount_paid} (${student.fee_amount || 0})`,
                domain: student.selected_course?.course_name || 'N/A',
                student_code: student.student_code || "N/A",
                status: student?.status ? "Active" : "In-Active" || "N/A",
                edit: permissions.includes("editstudentsID") ? (
                    <svg
                        onClick={(e) => {
                            e.stopPropagation();
                            navigate(`/user/editstd/${student.id}`);
                        }}
                        xmlns="http://www.w3.org/2000/svg"
                        width="24"
                        height="24"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        className="icon icon-tabler icons-tabler-outline icon-tabler-edit"
                    >
                        <path stroke="none" d="M0 0h24v24H0z" fill="none" />
                        <path d="M7 7h-1a2 2 0 0 0 -2 2v9a2 2 0 0 0 2 2h9a2 2 0 0 0 2 -2v-1" />
                        <path d="M20.385 6.585a2.1 2.1 0 0 0 -2.97 -2.97l-8.415 8.385v3h3l8.385 -8.415z" />
                        <path d="M16 5l3 3" />
                    </svg>
                ) : null,
                view: permissions.includes("showstudentspunchID") ? (
                    <button
                        className='btn btn-success h-75 d-flex align-items-center'
                        onClick={(e) => {
                            e.stopPropagation();
                            navigate(`/user/stdaylist/${student.id}`);
                        }}
                    >
                        View Punch
                    </button>
                ) : null
            }));

            setStudents(formattedStudents);
        } catch (error) {
            setError(error.message);
            toast.error(error.message || 'Failed to fetch students');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (permissions.includes("showstudentsID")) {
            fetchBatches();
            fetchStudents();
        } else {
            setLoading(false);
        }
    }, [permissions]);

    // Handle page change
    const handlePageChange = (newPage) => {
        setCurrentPage(newPage);
        setSearchParams({ page: newPage });
    };

    // Handle search input change with debounce
    const handleSearchChange = (event) => {
        const value = event.target.value;
        setSearchTerm(value);

        if (searchTimeout) {
            clearTimeout(searchTimeout);
        }

        const timeout = setTimeout(() => {
            if (permissions.includes("showstudentsID")) {
                fetchStudents(value, selectedCourseId);
                setCurrentPage(1);
                setSearchParams({ page: 1 });
            }
        }, 1000);

        setSearchTimeout(timeout);
    };

    // Handle course selection change
    const handleCourseChange = (event) => {
        const courseId = event.target.value;
        setSelectedCourseId(courseId);
        if (permissions.includes("showstudentsID")) {
            fetchStudents(searchTerm, courseId);
            setCurrentPage(1);
            setSearchParams({ page: 1 });
        }
    };

    // Delete confirmation handler
    const handleDeleteConfirm = async (e) => {
        e.preventDefault();
        if (!selectedStudent) return;

        try {
            const accessToken = localStorage.getItem('accessToken');
            const response = await fetch(`${process.env.REACT_APP_API}/api/course/students/${selectedStudent.id}`, {
                method: 'DELETE',
                headers: {
                    'Authorization': `Bearer ${accessToken}`,
                    'Content-Type': 'application/json',
                },
            });

            if (!response.ok) {
                throw new Error('Failed to delete student');
            }

            setStudents(prevStudents => prevStudents.filter(s => s.id !== selectedStudent.id));
            toast.success('Student deleted successfully');
            setSelectedStudent(null);
        } catch (error) {
            toast.error('Error deleting student');
        }
    };

    return (
        <div className="px-4 py-3 w-100">
            <h2>Total Students</h2>
            {permissions.includes("showstudentsID") && (
                <div className="w-100 d-flex justify-content-between align-items-center py-3">
                    <input
                        type="text"
                        className="form-control w-50"
                        placeholder="Search students..."
                        value={searchTerm}
                        onChange={handleSearchChange}
                    />
                    <div className='d-flex gap-3'>
                        <select
                            className="form-select"
                            value={selectedCourseId}
                            onChange={handleCourseChange}
                        >
                            <option value="">All Courses</option>
                            {batches.map(batch => (
                                <option key={batch.id} value={batch.course.id}>
                                    {batch.course.course_name}
                                </option>
                            ))}
                        </select>
                        {permissions.includes("addstudentsID") && (
                            <button
                                className="btn btn-success text-nowrap"
                                style={{ fontSize: "clamp(12px, 1.4vw, 16px)" }}
                                onClick={() => navigate('/user/addstd')}
                            >
                                Add Student <IconPlus size={20} stroke={2} />
                            </button>
                        )}
                    </div>
                </div>
            )}
            <div className='w-100'>
                {loading ? (
                    <Spinner />
                ) : permissions.includes("showstudentsID") ? (
                    students.length === 0 ? (
                        <div className="d-flex justify-content-center align-items-end position-relative" style={{ height: "50vh" }}>
                            <div className="position-absolute top-0 start-0 w-100 h-100" style={{
                                backgroundImage: 'url(/assets/file.png)',
                                backgroundSize: 'contain',
                                backgroundPosition: 'center',
                                backgroundRepeat: 'no-repeat',
                                opacity: 0.3,
                            }}></div>
                            <p className="m-0 fw-light fs-5 position-relative">No Students Found !</p>
                        </div>
                    ) : (
                        <Table
                            product={students}
                            header={[
                                { name: "count", label: "#", active: true },
                                { name: "student_code", label: "St Code", active: true },
                                { name: "name", label: "Name", active: true },
                                { name: "contact", label: "Number", active: true },
                                { name: "fee", label: "Fee", active: true },
                                { name: "domain", label: "Domain", active: true },
                                { name: "status", label: "Status", active: true },
                                { name: "edit", label: "", active: permissions.includes("editstudentsID") },
                                { name: "view", label: "", active: permissions.includes("showstudentspunchID") }
                            ]}
                            loading={loading}
                            onRowClick={permissions.includes("showstudentdetailsID") ? (item) => navigate(`/user/student/${item.id}`) : () => {}}
                            currentPage={currentPage}
                            onPageChange={handlePageChange}
                        />
                    )
                ) : (
                    <p>No permission to view students.</p>
                )}
            </div>

            <div className="modal fade" id="deleteModal" tabIndex="-1" aria-hidden="true">
                <div className="modal-dialog">
                    <div className="modal-content">
                        <div className="modal-header">
                            <h5 className="modal-title">Confirm Deletion</h5>
                            <button type="button" className="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
                        </div>
                        <div className="modal-body">
                            Are you sure you want to delete {selectedStudent?.name}?
                        </div>
                        <div className="modal-footer">
                            <button type="button" className="btn btn-secondary" data-bs-dismiss="modal">Cancel</button>
                            <button type="button" className="btn btn-danger" onClick={handleDeleteConfirm} data-bs-dismiss="modal">Confirm</button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}